import { useCallback } from 'react';

import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { useAppDispatch } from '@core/store';
import { getLocalStorageItem } from '@utils/storage';

import useComponentAction, {
  ComponentActionResultKind
} from '../../hooks/useComponentAction';
import useCustomActionModalTarget from '../../hooks/useCustomActionModalTarget';
import { dismissBanner } from '../../slices/dynamicComponentsSlice';
import { BannerData, ComponentInstance, ComponentType } from '../../types';
import { dismiss } from '../../utils/dismissalPersistence';

const useBannerActions = (banners: ComponentInstance<BannerData>[]) => {
  const dispatch = useAppDispatch();
  const { handleAction, isActionValid } = useComponentAction();
  const customModal = useCustomActionModalTarget();

  const handleBannerAction = useCallback(
    (id: string) => {
      const banner = banners.find((b) => b.id === id);
      if (!banner?.data.button) return;

      const result = handleAction(banner.data.button);
      if (result.kind === ComponentActionResultKind.CUSTOM) {
        if (result.target === 'submit-kyc-modal') {
          sendMixpanelEvent(Event.ADD_KYC_INFO_CLICKED, {
            user_id: getLocalStorageItem(STORAGE_KEYS.USER_ID),
            organizationId: getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID)
          });
        }
        customModal.setTargetId(result.target);
      }
    },
    [banners, handleAction, customModal]
  );

  const handleBannerDismiss = useCallback(
    (id: string) => {
      const banner = banners.find((b) => b.id === id);
      if (banner?.is_one_time) {
        dismiss(ComponentType.BANNER, id);
      }
      dispatch(dismissBanner(id));
    },
    [banners, dispatch]
  );

  const isBannerActionValid = useCallback(
    (id: string): boolean => {
      const banner = banners.find((b) => b.id === id);
      if (!banner?.data.button) return false;
      return isActionValid(banner.data.button);
    },
    [banners, isActionValid]
  );

  return {
    activeModal: customModal.targetId,
    closeModal: customModal.clearTarget,
    handleBannerAction,
    handleBannerDismiss,
    isBannerActionValid
  };
};

export default useBannerActions;
